import { eligibleIdsForWrittenTargets } from "./resolveWithValue.helpers";

/** The fields of a matching ("apply to others") row that the server's checks read. */
export interface OtherRowCandidate {
  id: string;
  field: string;
  eManifestValue?: string | null;
  vendorValue?: string | null;
  manifestDocumentValue?: string | null;
  vendorAlignmentSuspect?: boolean | null;
  vendorWasteLinesUnmatched?: boolean | null;
}

/** Mirrors the server's normalizeCompareValue: trimmed, with blank and "[]" meaning no value. */
function serverCompareValue(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed === "" || trimmed === "[]" ? null : trimmed;
}

/**
 * The matching rows an EMANIFEST or VENDOR answer would write, mirroring
 * resolveDiscrepancies' checks: the chosen source must hold a value
 * (assertSourceHasValue), a DOCUMENT target can't already say that value
 * (assertDocumentTargetNotAlreadyCorrect), and each written target's own
 * eligibility applies. The server rejects the whole call for one failing row.
 */
export function eligibleOtherIdsForSource(
  source: "EMANIFEST" | "VENDOR",
  rows: readonly OtherRowCandidate[],
  writesVendor: boolean,
  writesDocument: boolean
): string[] {
  const valueOf = (r: OtherRowCandidate) =>
    source === "EMANIFEST" ? r.eManifestValue : r.vendorValue;
  const withValue = rows.filter((r) => valueOf(r) != null);
  const targetEligible = eligibleIdsForWrittenTargets(
    withValue,
    false,
    writesVendor,
    writesDocument
  );
  return withValue
    .filter((r) => targetEligible === null || targetEligible.has(r.id))
    .filter(
      (r) =>
        !writesDocument ||
        serverCompareValue(valueOf(r)) !== serverCompareValue(r.manifestDocumentValue)
    )
    .map((r) => r.id);
}
